import { useCurrencies } from '@/hooks/useCurrencies';
import { useEffect, useState } from 'react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { WalletLedgerTable } from '@/features/audit-trail';
import type { ClientAccount } from '../types';
import type { CreateAccountPayload } from '../useClientDetail';
import { formatMonetary } from '../validation';

interface AccountsTabProps {
  accounts: ClientAccount[];
  loading: boolean;
  onLoad: () => Promise<void>;
  onCreate: (payload: CreateAccountPayload) => Promise<void>;
}

export function AccountsTab({ accounts, loading, onLoad, onCreate }: AccountsTabProps) {
  const { codes: currencyCodes, unavailable: currenciesUnavailable } = useCurrencies();
  const [showModal, setShowModal] = useState(false);
  const [currency, setCurrency] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ledgerWalletId, setLedgerWalletId] = useState<string | null>(null);

  useEffect(() => {
    void onLoad();
  }, [onLoad]);

  useEffect(() => {
    if (!currencyCodes.includes(currency)) setCurrency(currencyCodes[0] ?? '');
  }, [currencyCodes, currency]);

  const handleCreate = async () => {
    if (currenciesUnavailable || !currencyCodes.includes(currency)) return;
    setSubmitting(true);
    try {
      await onCreate({ currency });
      setShowModal(false);
    } catch {
      // Error already toasted
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="md" text="Loading accounts..." />;
  }

  return (
    <div>
      <div className="mb-4">
        <Button
          size="sm"
          className="bg-[#2e7d32] hover:bg-[#1b5e20]"
          onClick={() => setShowModal(true)}
        >
          + Create Account
        </Button>
      </div>

      {accounts.length === 0 ? (
        <div className="rounded-xl border border-[#eee] bg-white p-10 text-center text-[14px] text-[#999]">
          No accounts created yet
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
          {accounts.map((account) => (
            <div
              key={account.id}
              data-testid={`account-card-${account.id}`}
              className="rounded-xl border border-[#eee] bg-white p-6"
            >
              <div className="mb-4 flex items-center justify-between">
                <span className="text-[18px] font-extrabold text-[#1a1a1a]">
                  {account.currency}
                </span>
                {account.wallet_id !== undefined &&
                  account.wallet_id !== null &&
                  String(account.wallet_id).trim() !== '' && (
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`account-ledger-btn-${account.id}`}
                      onClick={() => setLedgerWalletId(String(account.wallet_id))}
                    >
                      View ledger
                    </Button>
                  )}
              </div>
              <div className="mb-3">
                <div className="text-[11px] uppercase tracking-[0.5px] text-[#999]">Available</div>
                <div className="text-[28px] font-extrabold text-[#2e7d32]">
                  {formatMonetary(account.available_balance, account.currency)}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">
                    Pending In
                  </div>
                  <div className="text-[13px] font-medium text-[#1a1a1a]">
                    {formatMonetary(account.pending_in_balance, account.currency)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">
                    Pending Out
                  </div>
                  <div className="text-[13px] font-medium text-[#1a1a1a]">
                    {formatMonetary(account.pending_out_balance, account.currency)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">Reserved</div>
                  <div className="text-[13px] font-medium text-[#1a1a1a]">
                    {formatMonetary(account.reserved_balance, account.currency)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">IBAN</div>
                  <div className="text-[12px] font-medium text-[#1a1a1a] font-mono">
                    {account.iban || '—'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Account Modal */}
      <Modal
        isOpen={showModal}
        title="Create Account"
        onClose={() => setShowModal(false)}
        footer={
          <>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-[#2e7d32] hover:bg-[#1b5e20]"
              onClick={() => void handleCreate()}
              disabled={submitting || currenciesUnavailable}
            >
              {submitting ? 'Creating...' : 'Create'}
            </Button>
          </>
        }
      >
        <div>
          <label className="mb-1 block text-[12px] font-semibold text-[#666]">Currency</label>
          <select
            disabled={currenciesUnavailable}
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-full rounded-lg border border-[#e0e0e0] px-4 py-2.5 text-sm outline-none focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
          >
            {currencyCodes.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </Modal>

      <Modal
        isOpen={ledgerWalletId !== null}
        title="Wallet ledger"
        onClose={() => setLedgerWalletId(null)}
        className="max-w-3xl"
        footer={
          <Button variant="outline" onClick={() => setLedgerWalletId(null)}>
            Close
          </Button>
        }
      >
        <div data-testid="wallet-ledger-modal-body">
          {ledgerWalletId !== null && <WalletLedgerTable walletId={ledgerWalletId} />}
        </div>
      </Modal>
    </div>
  );
}
